import { AllyBase,  AllyObject, AllyId, AllyAttribute, AllyZone } from "../classes";
import { AllyNode } from "../types";

/**
 * This class contains functions specific to angular.
 * 
 * @extends AllyBase
 */
export class AllyAngular extends AllyBase {
    private id: string;
    private static ids: AllyId = new AllyId("lib", AllyBase.bridge);
    /**
     * @param bridge The bridge to the parent in the hierarchical tree.
     * @see AllyBase
     */
    constructor(bridge: () => AllyBase) {
        super(bridge);
        this.id = AllyAngular.ids.getId();
        this.destroyer.addCallback(() => {
            AllyAngular.ids.returnId(this.id);
        });
    }
    /**
     * This function retrieves the angular host id of a node.
     * @param node The node the id is to be retrieved from.
     * @returns The id that was retrieved or null if no id was found.
     */
    public getHost(node: AllyNode) {
        let result = null;
        if (node.attributes) {
            for (let i = 0; i < node.attributes.length; i++) {
                if (node.attributes[i].name.includes("_nghost-ng-")) {
                    result = node.attributes[i].name;
                }
            }
        }
        return result;
    }
    /**
     * Checks if a node is a menu trigger.
     * @param node The node to be checked.
     * @returns Returns True if the node is a menu trigger.
     */
    public isMenuRoot(node: AllyNode) {
        return node.ariaHasPopup === "menu";
    }
    /**
     * Retrieves the root node of the submenu triggered by the node.
     * @param node The trigger node of the submenu.
     * @returns Returns the root node of the submenu or null if there is no root node.
     */
    public getSubMenu(node: AllyNode): AllyNode | null {
        if (node.ariaControlsElements && node.ariaControlsElements[0]) {
            return node.ariaControlsElements[0] as AllyNode;
        } else {
            return null;
        }
    }
    /**
     * Checks if a node is the root node of a layer.
     * @param object The object whose node is to be checked.
     * @returns Returns true if the node is the root node of a layer.
     */
    public isLayer(object: AllyObject) {
        return (object.node.getAttribute && object.node.getAttribute("id")?.includes("cdk-overlay"))
            && (!object.node.querySelector || !object.node.querySelector("& > mat-tooltip-component"))
            && this.hasBackdrop(object.node)
            && !(document.querySelector('[' + this.id + '-layer] [adx-ally-id="' + object.id + '"]'))
        || object.node.localName === "app-root";
    }
    // Only an overlay with a backdrop is modal.
    private hasBackdrop(node: AllyNode) {
        const pane = node as unknown as Element;
        // CDK 22 puts the backdrop next to the pane, older versions before the host.
        return [pane.previousElementSibling, pane.parentElement?.previousElementSibling]
            .some((sibling) => sibling?.classList.contains("cdk-overlay-backdrop") ?? false);
    }
    public markLayer(object: AllyObject) {
        if (object.variables[this.id + "-index"] === undefined) {
            object.variables[this.id + "-index"] = object.attribute.createIndex(0);
        }
        let index = object.variables[this.id + "-index"] as AllyAttribute["index"];
        object.attribute.setAttribute(this.id + "-layer", index, "");
    }
    /**
     * This function removes the residual properties created by Angular that are created when an eventListener is added to a node.
     * @param node The node the residual properties are to be removed from.
     * @param eventType The event type of the eventListener.
     * @see AllyZone
     */
    public cleanUpListener(node: AllyNode, eventType: string) {
        AllyZone.cleanUpListener(node, eventType);
    }
    /**
     * This function checks if a node has an eventListener listening for click events.
     * @param node The node that is to be checked.
     * @returns Returns True if the node has an eventListener listening for click events.
     * @see AllyZone
     */
    public hasClickListener(node: AllyNode) {
        return AllyZone.hasClickListener(node);
    }
    /**
     * This function triggers a click event on a node.
     * @param node The node the event is triggered on.
     */
    public click(node: AllyNode) {
        node.click && node.click();
    }
    public isSelect(object: AllyObject) {
        return object.node.localName === "mat-select";
    }
    public isChip(node: AllyNode) {
        return node.localName === "mat-chip";
    }
    public isRippleLoaderDisabled(name: string) {
        return name === "mat-ripple-loader-disabled";
    }
}